import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  alpha,
  CircularProgress,
  Zoom,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
} from "@mui/material";
import {
  Delete,
  CloudUpload,
  ErrorOutline,
  Image,
  Search,
  Close,
  Edit,
  Save,
} from "@mui/icons-material";
import {
  fetchMedia,
  deleteMedia,
  uploadMedia,
  updateMedia,
} from "../../api/superAdmin";

const ITEMS_PER_PAGE = 24;

const searchableText = (item: any) =>
  [
    item.title,
    item.alt_text,
    item.original_filename,
    item.filename,
    item.key,
    item.url,
    ...(Array.isArray(item.tags) ? item.tags : []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const displayTitle = (item: any) =>
  item.title || item.original_filename || item.filename || "Untitled image";

const parseTags = (value: string) =>
  value
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);

const MediaLibraryPage = () => {
  const [media, setMedia] = useState<any[]>([]);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [selectedImage, setSelectedImage] = useState<any | null>(null);
  const [editingImage, setEditingImage] = useState<any | null>(null);
  const [savingMeta, setSavingMeta] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);

  const [editTitle, setEditTitle] = useState("");
  const [editAltText, setEditAltText] = useState("");
  const [editTags, setEditTags] = useState("");

  const load = async () => {
    const data = await fetchMedia();
    setMedia(data || []);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const filteredMedia = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return media;
    return media.filter((item) => searchableText(item).includes(q));
  }, [media, searchQuery]);

  const totalPages = Math.ceil(filteredMedia.length / ITEMS_PER_PAGE);
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const paginatedMedia = filteredMedia.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const openEditDialog = (item: any) => {
    setEditingImage(item);
    setEditTitle(item.title || "");
    setEditAltText(item.alt_text || "");
    setEditTags(Array.isArray(item.tags) ? item.tags.join(", ") : "");
  };

  const saveMetadata = async () => {
    if (!editingImage) return;

    setSavingMeta(true);
    try {
      const updated = await updateMedia(editingImage.id, {
        title: editTitle,
        alt_text: editAltText,
        tags: parseTags(editTags),
      });

      setMedia((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setEditingImage(null);
    } catch (err: any) {
      setDeleteError(err?.response?.data?.error || "Failed to update media");
    } finally {
      setSavingMeta(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, margin: "0 auto" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        mb={4}
        spacing={2}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Image sx={{ fontSize: 32, color: "primary.main" }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Media Library
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredMedia.length} {filteredMedia.length === 1 ? "file" : "files"}
            </Typography>
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            size="small"
            placeholder="Search title, tags, filename..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")}>
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ minWidth: { xs: "100%", sm: 320 }, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />

          <Button
            component="label"
            variant="contained"
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
            disabled={uploading}
            sx={{ px: 3, py: 1.2, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            {uploading ? "Uploading..." : "Upload Media"}
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={async (e) => {
                if (e.target.files?.[0]) {
                  setUploading(true);
                  try {
                    await uploadMedia(e.target.files[0]);
                    await load();
                  } catch (err) {
                    setDeleteError("Failed to upload file");
                  } finally {
                    setUploading(false);
                    e.target.value = "";
                  }
                }
              }}
            />
          </Button>
        </Stack>
      </Stack>

      {filteredMedia.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 10,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
            borderRadius: 3,
            border: (theme) => `2px dashed ${alpha(theme.palette.primary.main, 0.15)}`,
          }}
        >
          <Image sx={{ fontSize: 64, color: (theme) => alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No media found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload media or try a different search.
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={2.5}>
            {paginatedMedia.map((m: any) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={m.id}>
                <Zoom in timeout={300}>
                  <Paper
                    elevation={0}
                    sx={{
                      position: "relative",
                      borderRadius: 2,
                      overflow: "hidden",
                      transition: "all 0.25s",
                      border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: (theme) => `0 12px 24px ${alpha(theme.palette.common.black, 0.12)}`,
                        "& .media-actions": { opacity: 1 },
                      },
                    }}
                  >
                    <Box
                      onClick={() => setSelectedImage(m)}
                      sx={{ position: "relative", paddingTop: "75%", bgcolor: 'action.hover', cursor: "pointer" }}
                    >
                      <img
                        src={m.url}
                        alt={m.alt_text || displayTitle(m)}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        loading="lazy"
                      />

                      <Stack
                        className="media-actions"
                        direction="row"
                        spacing={1}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          opacity: { xs: 1, md: 0 },
                          transition: "opacity 0.2s",
                        }}
                      >
                        <Tooltip title="Edit details">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditDialog(m);
                            }}
                            sx={{ bgcolor: "rgba(255,255,255,0.92)", "&:hover": { bgcolor: "primary.main", color: "white" } }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete image">
                          <IconButton
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                setDeletingId(m.id);
                                await deleteMedia(m.id);
                                await load();
                              } catch (err: any) {
                                setDeleteError(err?.response?.data?.error || "Failed to delete image");
                              } finally {
                                setDeletingId(null);
                              }
                            }}
                            disabled={deletingId === m.id}
                            sx={{ bgcolor: "rgba(255,255,255,0.92)", "&:hover": { bgcolor: "error.main", color: "white" } }}
                          >
                            {deletingId === m.id ? <CircularProgress size={20} color="error" /> : <Delete fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>

                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {displayTitle(m)}
                      </Typography>

                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {m.original_filename || m.filename || "No filename"}
                      </Typography>

                      {Array.isArray(m.tags) && m.tags.length > 0 && (
                        <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap">
                          {m.tags.slice(0, 3).map((tag: string) => (
                            <Chip key={tag} label={tag} size="small" />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Paper>
                </Zoom>
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination count={totalPages} page={page} onChange={(_, newPage) => setPage(newPage)} color="primary" showFirstButton showLastButton />
            </Box>
          )}
        </>
      )}

      <Dialog open={!!selectedImage} onClose={() => setSelectedImage(null)} maxWidth="md" fullWidth>
        <Box sx={{ position: "relative", bgcolor: "grey.900" }}>
          <img
            src={selectedImage?.url || ""}
            alt={selectedImage?.alt_text || "Preview"}
            style={{ width: "100%", maxHeight: "70vh", objectFit: "contain", display: "block" }}
          />
          <IconButton
            onClick={() => setSelectedImage(null)}
            sx={{ position: "absolute", top: 12, right: 12, bgcolor: "rgba(0,0,0,0.5)", color: "white" }}
          >
            <Close />
          </IconButton>
        </Box>
      </Dialog>

      <Dialog open={!!editingImage} onClose={() => setEditingImage(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Media Details</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editingImage?.url && (
              <Box component="img" src={editingImage.url} alt="" sx={{ width: "100%", maxHeight: 220, objectFit: "contain", bgcolor: 'action.hover', borderRadius: 2 }} />
            )}

            <TextField label="Title" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} fullWidth />
            <TextField label="Alt text" value={editAltText} onChange={(e) => setEditAltText(e.target.value)} fullWidth multiline minRows={2} />
            <TextField
              label="Tags"
              value={editTags}
              onChange={(e) => setEditTags(e.target.value)}
              fullWidth
              helperText="Comma separated, e.g. disposable, strawberry, homepage"
            />

            <Typography variant="caption" color="text.secondary">
              Original file: {editingImage?.original_filename || editingImage?.filename || "Unknown"}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingImage(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveMetadata} disabled={savingMeta} startIcon={savingMeta ? <CircularProgress size={16} /> : <Save />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteError} onClose={() => setDeleteError(null)} maxWidth="xs" fullWidth>
        <Box sx={{ textAlign: "center", pt: 3, pb: 1 }}>
          <ErrorOutline sx={{ fontSize: 44, color: "error.main", mb: 1 }} />
          <DialogTitle>Cannot Delete Image</DialogTitle>
          <DialogContent>
            <Typography color="text.secondary" variant="body2">
              {deleteError}
            </Typography>
          </DialogContent>
        </Box>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button onClick={() => setDeleteError(null)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MediaLibraryPage;